import { useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import logoWithTextWhite from '@/assets/images/pliable/logo_with_text_white.png';
import logoWithTextBlack from '@/assets/images/pliable/logo_with_text_black.png';

/**
 * The full logo — wordmark included — to show for this tenant.
 *
 * A whitelabeled tenant gets their own logo and must never see Pliable's, so
 * this is not something to resolve by importing the asset at a call site.
 * `__PLB_ENV__` is checked before the config API because on a custom domain the
 * API answer arrives a beat late, and the gap is long enough to flash the wrong
 * brand at the customer.
 *
 * Which Pliable logo is a question about the surface behind it, not the theme:
 * the sidebar is dark in both themes and so always wants the white one, while
 * a page that follows the theme wants whichever contrasts today. Hence the
 * flag rather than reading the theme alone.
 */
export function useBrandLogo({ onDarkSurface = false }: { onDarkSurface?: boolean } = {}): string {
  const { theme } = useTheme();
  const { data: tenantConfig, dataUpdatedAt } = useTenantConfig();

  return useMemo(() => {
    const wl = (tenantConfig as any)?.whitelabeling;
    if (wl?.enabled !== false && wl?.logo_path) {
      return wl.logo_path;
    }
    const envWl = (window as any).__PLB_ENV__?.whitelabeling;
    if (envWl?.logo_path) {
      return envWl.logo_path;
    }
    return onDarkSurface || theme === 'dark' ? logoWithTextWhite : logoWithTextBlack;
  }, [dataUpdatedAt, tenantConfig, theme, onDarkSurface]);
}
